import { Component } from '@angular/core';
import { TableModule } from 'primeng/table';
import { SkillCategory } from '../../../core/models/skill-category';
import { SkillCategoryService } from '../../../core/services/skill-category.service';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { ToastModule } from 'primeng/toast';
import { ConfirmationService, MessageService } from 'primeng/api';
import { DialogModule } from 'primeng/dialog';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { SkillCategoryFormComponent } from './skill-category-form/skill-category-form.component';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { EmPermissionDirective } from '../../../shared/directives/has-permission.directive';
import { Permission } from '../../../core/enums/permission.enum';

@Component({
  selector: 'em-skill-category',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TableModule,
    ButtonModule,
    InputTextModule,
    ToastModule,
    DialogModule,
    ConfirmDialogModule,
    TranslatePipe,
    EmPermissionDirective,
  ],
  templateUrl: './skill-category.component.html',
  styleUrl: './skill-category.component.scss',
  providers: [MessageService, ConfirmationService],
})
export class SkillCategoryComponent {
  skillCategories!: SkillCategory[];
  ref: DynamicDialogRef | undefined;
  translations: any = {};
  Permission = Permission;

  constructor(
    private skillCategoryService: SkillCategoryService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private dialogService: DialogService,
    private translate: TranslateService,
  ) {}

  ngOnInit() {
    this.loadTranslations();
    this.getAllSkillCategories();
  }

  loadTranslations() {
    this.translate
      .get([
        'layout.configurations.skillCategories.addSkillCategory.title',
        'layout.configurations.skillCategories.addSkillCategory.successMessage.title',
        'layout.configurations.skillCategories.addSkillCategory.successMessage.detail',
        'layout.configurations.skillCategories.editSkillCategory.title',
        'layout.configurations.skillCategories.editSkillCategory.successMessage.title',
        'layout.configurations.skillCategories.editSkillCategory.successMessage.detail',
        'layout.configurations.skillCategories.deleteSkillCategory.title',
        'layout.configurations.skillCategories.deleteSkillCategory.successMessage.title',
        'layout.configurations.skillCategories.deleteSkillCategory.successMessage.detail',
        'layout.configurations.skillCategories.deleteSkillCategory.message',
        'layout.configurations.skillCategories.deleteSkillCategory.yes',
        'layout.configurations.skillCategories.deleteSkillCategory.no',
        'layout.configurations.skillCategories.deleteSkillCategory.errorMessage.title',
        'layout.configurations.skillCategories.deleteSkillCategory.errorMessage.detail',
      ])
      .subscribe((translations) => {
        this.translations = translations;
      });
  }

  getAllSkillCategories() {
    this.skillCategoryService.getAllSkillCategories().subscribe((data) => (this.skillCategories = data));
  }

  onAdd() {
    this.ref = this.dialogService.open(SkillCategoryFormComponent, {
      header: this.translations['layout.configurations.skillCategories.addSkillCategory.title'],
      width: '320px',
      contentStyle: { overflow: 'auto' },
    });

    this.ref.onClose.subscribe((skillCategory) => {
      if (skillCategory) {
        this.getAllSkillCategories();
        this.messageService.add({
          severity: 'success',
          summary: this.translations['layout.configurations.skillCategories.addSkillCategory.successMessage.title'],
          detail: this.translations['layout.configurations.skillCategories.addSkillCategory.successMessage.detail'],
        });
      }
    });
  }

  onEdit(skillCategory: SkillCategory) {
    this.ref = this.dialogService.open(SkillCategoryFormComponent, {
      header: this.translations['layout.configurations.skillCategories.editSkillCategory.title'],
      width: '320px',
      contentStyle: { overflow: 'auto' },
      data: skillCategory,
    });

    this.ref.onClose.subscribe((result) => {
      if (!result) return;

      if (result.type === 'updated') {
        this.getAllSkillCategories();
        this.messageService.add({
          severity: 'success',
          summary: this.translations['layout.configurations.skillCategories.editSkillCategory.successMessage.title'],
          detail: this.translations['layout.configurations.skillCategories.editSkillCategory.successMessage.detail'],
        });
      } else if (result.type === 'no-change') {
        this.messageService.add({
          severity: 'warn',
          summary: this.translate.instant('layout.configurations.users.assignRolesToUser.roles.noChanges'),
          detail: this.translate.instant('layout.configurations.users.assignRolesToUser.roles.noUpdateMessage'),
        });
      }
    });
  }

  onDelete(event: Event, id: number) {
    this.confirmationService.confirm({
      target: event.target as EventTarget,
      message: this.translations['layout.configurations.skillCategories.deleteSkillCategory.message'],
      icon: 'pi pi-info-circle',
      header: this.translations['layout.configurations.skillCategories.deleteSkillCategory.title'],
      acceptIcon: 'none',
      rejectIcon: 'none',
      acceptLabel: this.translations['layout.configurations.skillCategories.deleteSkillCategory.yes'],
      rejectLabel: this.translations['layout.configurations.skillCategories.deleteSkillCategory.no'],
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        this.deleteSkillCategory(id);
      },
      reject: () => {
        this.messageService.add({
          severity: 'info',
          summary: this.translate.instant('layout.configurations.skillCategories.deleteSkillCategory.info.title'),
          detail: this.translate.instant('layout.configurations.skillCategories.deleteSkillCategory.info.alreadyExists'),
          life: 3000,
        });
      },
    });
  }

  deleteSkillCategory(id: number) {
    this.skillCategoryService.deleteSkillCategory(id).subscribe({
      next: () => {
        this.getAllSkillCategories();
        this.messageService.add({
          severity: 'success',
          summary: this.translations['layout.configurations.skillCategories.deleteSkillCategory.successMessage.title'],
          detail: this.translations['layout.configurations.skillCategories.deleteSkillCategory.successMessage.detail'],
        });
      },
      error: (err) => {
        console.log('Error deleting skill category: ' + err);
        this.messageService.add({
          severity: 'error',
          summary: this.translations['layout.configurations.skillCategories.deleteSkillCategory.errorMessage.title'],
          detail: this.translations['layout.configurations.skillCategories.deleteSkillCategory.errorMessage.detail'],
        });
      },
    });
  }
}
